/**
 * useMetaTags Hook
 *
 * Manages meta tags for SEO and social media sharing.
 * React 19 compatible alternative to react-helmet-async.
 */

import { useEffect } from 'react';

export interface MetaTagsConfig {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  twitterSite?: string;
  keywords?: string;
  author?: string;
}

const DEFAULT_CONFIG: Required<Omit<MetaTagsConfig, 'keywords' | 'author'>> = {
  title: 'Smart Prompt: One Trusted Answer from Many AIs',
  description: 'Compare responses from Claude, GPT-4, Gemini, and more AI models side-by-side. Find the best AI for your needs with Smart Prompt.',
  image: '/og-image.svg',
  url: 'https://smart-prompt.com',
  type: 'website',
  twitterCard: 'summary_large_image',
  twitterSite: '@smartprompt',
};

/**
 * Updates meta tags in the document head
 */
export function useMetaTags(config: MetaTagsConfig = {}) {
  useEffect(() => {
    const mergedConfig = { ...DEFAULT_CONFIG, ...config };

    // Update title
    if (mergedConfig.title) {
      document.title = mergedConfig.title;
    }

    // Store original tags to restore on unmount
    const originalTags = new Map<string, string>();

    // Helper to set or update a meta tag
    const setMetaTag = (property: string, content: string, isProperty = true) => {
      const selector = isProperty
        ? `meta[property="${property}"]`
        : `meta[name="${property}"]`;

      let tag = document.querySelector(selector) as HTMLMetaElement;

      if (tag) {
        originalTags.set(property, tag.content);
        tag.content = content;
      } else {
        tag = document.createElement('meta');
        if (isProperty) {
          tag.setAttribute('property', property);
        } else {
          tag.setAttribute('name', property);
        }
        tag.content = content;
        document.head.appendChild(tag);
        originalTags.set(property, ''); // Mark for removal on unmount
      }
    };

    // Open Graph tags
    setMetaTag('og:title', mergedConfig.title);
    setMetaTag('og:description', mergedConfig.description);
    setMetaTag('og:image', mergedConfig.image);
    setMetaTag('og:url', mergedConfig.url);
    setMetaTag('og:type', mergedConfig.type);

    // Twitter Card tags
    setMetaTag('twitter:card', mergedConfig.twitterCard, false);
    setMetaTag('twitter:title', mergedConfig.title, false);
    setMetaTag('twitter:description', mergedConfig.description, false);
    setMetaTag('twitter:image', mergedConfig.image, false);
    setMetaTag('twitter:site', mergedConfig.twitterSite, false);

    // Standard meta tags
    setMetaTag('description', mergedConfig.description, false);

    if (config.keywords) {
      setMetaTag('keywords', config.keywords, false);
    }

    if (config.author) {
      setMetaTag('author', config.author, false);
    }

    // Cleanup function
    return () => {
      originalTags.forEach((originalContent, property) => {
        const isProperty = property.startsWith('og:');
        const selector = isProperty
          ? `meta[property="${property}"]`
          : `meta[name="${property}"]`;

        const tag = document.querySelector(selector) as HTMLMetaElement;

        if (tag) {
          if (originalContent === '') {
            // This tag was created by us, remove it
            tag.remove();
          } else {
            // Restore original content
            tag.content = originalContent;
          }
        }
      });
    };
  }, [config]);
}
